import {
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  useFairModeChoice,
  useThemeChoice,
  type Theme,
} from '@artosera/ui';
import { Moon, Sun, Tent } from 'lucide-react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DESIGNS_PROPOSES,
  designDescriptions,
  designNames,
  designsAffiches,
} from '../../appearance/designs.js';
import { useDesign } from '../../appearance/useDesign.js';
import type { DesignId } from '../../appearance/designs.js';
import { useTeinte, type Teinte } from '../../appearance/teinte.js';
import { useFairMode } from './hooks.js';
import { useEcranFoireImpose } from './useAutoFairLayout.js';
import styles from './MenuAffichage.module.css';

export interface MenuAffichageProps {
  compteId: string;
  langue: 'fr' | 'en';
  /** Le thème n'est réglable que si la console laisse le choix au client (lot C). */
  designLibre: boolean;
  /** Le mode foire est un module complémentaire (D21) : sans lui, pas d'interrupteur. */
  foireOuverte: boolean;
}

/** Les trois façons d'afficher la régie : deux thèmes, et la mise en page foire (sombre). */
type Apparence = Theme | 'foire';

/**
 * Réglages d'affichage rangés dans le menu de l'avatar : apparence (clair,
 * sombre, foire en un seul choix), design, teinte. Les entrées sont de vrais
 * éléments de menu (`menuitemradio`) : flèches, Entrée et Espace les
 * parcourent, et le menu reste ouvert pour que le changement se voie. Monté à
 * l'ouverture du menu.
 *
 * « Foire » = mise en page foire (`data-layout="fair"`) en thème sombre ;
 * « Clair » et « Sombre » ramènent la mise en page normale. Sous 1 024 px la
 * mise en page foire s'impose d'elle-même (`useAutoFairLayout`) : le choix se
 * réduit alors au thème, et « Foire » reste affiché, grisé, pour le dire.
 */
export function MenuAffichage({
  compteId,
  langue,
  designLibre,
  foireOuverte,
}: MenuAffichageProps): JSX.Element {
  const { t } = useTranslation('regie');
  const { theme, setTheme } = useThemeChoice();
  const { design, setDesign } = useDesign(compteId);
  const { teinte, setTeinte, teintes } = useTeinte(design, compteId);
  const { setFair } = useFairModeChoice();
  const foireAffichee = useFairMode();
  const foireImposee = useEcranFoireImpose();
  const apparence: Apparence = foireOuverte && foireAffichee && !foireImposee ? 'foire' : theme;

  function choisirApparence(valeur: Apparence): void {
    if (valeur === 'foire') {
      setTheme('dark');
      setFair(true);
      return;
    }
    setTheme(valeur);
    if (!foireImposee) setFair(false);
  }

  return (
    <>
      <DropdownMenuRadioGroup
        label={t('coquille.affichage.apparence')}
        value={apparence}
        onValueChange={(valeur) => choisirApparence(valeur as Apparence)}
      >
        <DropdownMenuRadioItem
          value="light"
          label={t('coquille.affichage.clair')}
          icon={<Sun size={16} strokeWidth={1.5} aria-hidden />}
        />
        <DropdownMenuRadioItem
          value="dark"
          label={t('coquille.affichage.sombre')}
          icon={<Moon size={16} strokeWidth={1.5} aria-hidden />}
        />
        {foireOuverte ? (
          <DropdownMenuRadioItem
            value="foire"
            label={t('coquille.affichage.foire')}
            description={
              foireImposee
                ? t('coquille.affichage.foireImposee')
                : t('coquille.affichage.foireAide')
            }
            icon={<Tent size={16} strokeWidth={1.5} aria-hidden />}
            disabled={foireImposee}
          />
        ) : null}
      </DropdownMenuRadioGroup>

      {designLibre ? (
        <DropdownMenuRadioGroup
          label={t('coquille.affichage.design')}
          value={design}
          onValueChange={(valeur) => setDesign(valeur as DesignId)}
        >
          {designsAffiches(design).map((option) => (
            <DropdownMenuRadioItem
              key={option}
              value={option}
              label={
                DESIGNS_PROPOSES.includes(option)
                  ? designNames[option]
                  : `${designNames[option]} ${t('coquille.affichage.plusPropose')}`
              }
              description={designDescriptions[langue][option]}
            />
          ))}
        </DropdownMenuRadioGroup>
      ) : null}

      {teinte !== null ? (
        <DropdownMenuRadioGroup
          label={t(
            design === 'liquid-glass'
              ? 'coquille.affichage.teinte'
              : 'coquille.affichage.teinteAccent',
          )}
          value={teinte}
          onValueChange={(valeur) => setTeinte(valeur as Teinte)}
        >
          {teintes.map((option) => (
            <DropdownMenuRadioItem
              key={option}
              value={option}
              label={t(`coquille.affichage.teintes.${option}`)}
              icon={
                <span
                  className={styles.pastille}
                  style={{
                    background:
                      design === 'liquid-glass'
                        ? `var(--design-lg-teinte-${option})`
                        : `var(--design-teinte-${design}-${option})`,
                  }}
                  aria-hidden
                />
              }
            />
          ))}
        </DropdownMenuRadioGroup>
      ) : null}
    </>
  );
}
